/**
 * A registry's answer as survey-api stored it, drawn whole — roadmap D8, on the person hub
 * (SR-215, the participant card of SR-213 block 8 before it), and the same way on the SEC ADV
 * firm card's prefill preview, both through `RegistryFact`, which folds it under "As stored".
 *
 * **Everything in it, and nothing picked out.** Every object member, every array element and
 * every scalar is drawn, at every depth; each object and array can be folded by its own summary
 * line, and inside the tree nothing starts folded. What staff read first is no longer this: it
 * is survey-api's projection of the answer (the person hub design §5), drawn above it where
 * survey-api made one. The tree stays under it as the evidence — and it stays whole, because
 * which fields matter is survey-api's reading to make, not a guess about a registry's JSON this
 * app would get wrong the day the registry changes. Nothing here projects, and nothing compares
 * FINRA's name with ours: survey-api's `name_match` does.
 *
 * **A string is shown as the string it is**, in quotes, even when it holds JSON: FINRA sends
 * its broker record as a JSON *string* inside the envelope, and decoding it would be this app
 * deciding how FINRA encodes, which holds only until it does not. The quotes are what tell
 * `"4210"` from `4210`; the text inside them is not escaped, so a record reads as a record.
 *
 * It takes `unknown` and judges nothing about its shape. Anything JSON cannot carry — the
 * member missing altogether — is shown as what JavaScript calls it rather than dropped.
 */
export function PayloadTree({ value, label }: { value: unknown; label: string }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="overflow-x-auto rounded-md bg-[var(--color-off-white)] p-3 font-mono text-xs"
    >
      <PayloadNode name={null} value={value} />
    </div>
  );
}

type Branch = unknown[] | Record<string, unknown>;

function isBranch(value: unknown): value is Branch {
  return typeof value === 'object' && value !== null;
}

function PayloadNode({ name, value }: { name: Nullable<string>; value: unknown }) {
  if (!isBranch(value)) return <PayloadLeaf name={name} value={value} />;

  const isArray = Array.isArray(value);
  const entries: [string, unknown][] = isArray
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);

  // An empty one has nothing to fold, so it is a leaf that says it is empty.
  if (entries.length === 0) {
    return <PayloadLeaf name={name} value={value} empty={isArray ? '[]' : '{}'} />;
  }

  const noun = isArray ? 'item' : 'member';
  const size = `${entries.length} ${noun}${entries.length === 1 ? '' : 's'}`;

  // `open` as the initial state only: React sets it once and leaves the element's own toggle
  // alone afterwards, so a node folded by hand stays folded when the hub re-reads itself.
  return (
    <details open>
      <summary className="cursor-pointer select-none">
        {name !== null && <PayloadKey name={name} />}
        <span className="text-muted-foreground">{size}</span>
      </summary>
      <div className="ml-1.5 grid gap-0.5 border-l border-border pl-3">
        {entries.map(([key, item]) => (
          <PayloadNode key={key} name={key} value={item} />
        ))}
      </div>
    </details>
  );
}

function PayloadKey({ name }: { name: string }) {
  return (
    <>
      <span className="text-[var(--color-royal-blue)]">{name}</span>
      <span className="mr-2 text-muted-foreground" aria-hidden>
        :
      </span>
    </>
  );
}

function PayloadLeaf({
  name,
  value,
  empty
}: {
  name: Nullable<string>;
  value: unknown;
  empty?: string;
}) {
  return (
    <div>
      {name !== null && <PayloadKey name={name} />}
      <span className="wrap-anywhere whitespace-pre-wrap">{empty ?? scalarText(value)}</span>
    </div>
  );
}

function scalarText(value: unknown): string {
  if (typeof value === 'string') return `"${value}"`;
  if (value === null) return 'null';
  return String(value);
}
